import type { MindItem } from '@kimuno/api-sdk/lib/types/responses/MindItem';
import { Box } from '@mui/material';
import _, { debounce } from 'lodash';
import Mousetrap from 'mousetrap';
import type { Instance } from 'pspdfkit';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ContentSearchedInside } from '../../../ampli';
import {
	borderRadius,
	boxShadows,
	customSizes,
	palette,
	spaces,
} from '../../../assets/styles/themes/tokens';
import services from '../../../services';
import { getAmpliContentData } from '../../../utils/ampli';
import useIntercom from '../../../utils/useIntercom';
import useScreenSize from '../../../utils/useScreenSize';
import { ReaderContext } from '../../Reader/ReaderContext';
import FloatingBarLargeContent from './FloatingToolbarLargeContent';
import FloatingBarSmallContent from './FloatingToolbarSmallContent';
import { focusOnCustomSearch, searchInputId } from './SearchInput';

interface OwnProps {
	mindItem: MindItem;
}

const focusOnCustomSearchOnKeyPress = (event: KeyboardEvent) => {
	focusOnCustomSearch();
	event.stopImmediatePropagation();
	event.preventDefault();
};

const debouncedTrackSearchedInsideEvent = debounce((searchTerm, mindItem) => {
	services.track.ampliEventTrack({
		event: new ContentSearchedInside({
			'search term': searchTerm,
			content: getAmpliContentData(mindItem, 'mindItem'),
		}),
	});
}, 3000);

const FloatingToolbarBase: React.FC<OwnProps> = ({ mindItem }) => {
	const { isMdScreen, isLgScreen } = useScreenSize();
	const isDesktopScreen = isLgScreen || isMdScreen;
	const { pspdfkitInstance, isTextBased, fullScreenMode, mindItemId } = useContext(ReaderContext);
	const { t } = useTranslation();

	const [searchResultIndex, setSearchResultIndex] = useState<number>();
	const [numberOfResults, setNumberOfResults] = useState<number>();
	const [searchTerm, setSearchTerm] = useState<string>();
	const { isIntercomVisible } = useIntercom();

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		handleSearchKeyboardShortcut();
		return () => unmountHandleDefaultSearchBehaviour();
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (searchTerm !== undefined) {
			handleSearch(pspdfkitInstance, searchTerm);
		}
	}, [mindItemId, pspdfkitInstance]);

	const getAdornment = () => {
		if (numberOfResults === undefined || searchResultIndex === undefined) {
			return;
		}
		return `${searchResultIndex + 1}/${numberOfResults}`;
	};

	const handleSearchKeyboardShortcut = () => {
		Mousetrap.bind('command+f', focusOnCustomSearchOnKeyPress);
		Mousetrap.bind('ctrl+f', focusOnCustomSearchOnKeyPress);

		const searchInput = document.getElementById(searchInputId);
		if (searchInput) {
			Mousetrap(searchInput).bind('command+f', focusOnCustomSearchOnKeyPress);
			Mousetrap(searchInput).bind('ctrl+f', focusOnCustomSearchOnKeyPress);
		}
	};

	const unmountHandleDefaultSearchBehaviour = () => {
		Mousetrap.unbind('command+f');
		Mousetrap.unbind('ctrl+f');
		const searchInput = document.getElementById(searchInputId);
		if (searchInput) {
			Mousetrap(searchInput).unbind('command+f');
			Mousetrap(searchInput).unbind('ctrl+f');
		}
	};

	const handleChangeEvent = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (!pspdfkitInstance) {
			return;
		}
		const query = event.target.value;
		setSearchTerm(query);
	};

	const handleSearch = (pspdfkitInstance: Instance | undefined, query: string) => {
		if (!pspdfkitInstance || !searchTerm) {
			return;
		}
		if (query.length < pspdfkitInstance.searchState.minSearchQueryLength) {
			setNumberOfResults(undefined);
			setSearchResultIndex(undefined);
			pspdfkitInstance.setSearchState(
				pspdfkitInstance.searchState.set('results', pspdfkitInstance.searchState.results.clear())
			);
			return;
		}

		pspdfkitInstance.search(query).then((results) => {
			setNumberOfResults(results.size);
			const currentPageIndex = pspdfkitInstance.viewState.currentPageIndex;

			const closestResult = results.find((result) => {
				const resultPageIndex = result.pageIndex;
				if (_.isNil(resultPageIndex)) {
					return false;
				}
				return resultPageIndex >= currentPageIndex;
			});

			const focusedResultIndex = closestResult ? results.indexOf(closestResult) : -1;

			const newSearchState = pspdfkitInstance.searchState
				.set('results', results)
				.set('focusedResultIndex', focusedResultIndex);

			setSearchResultIndex(focusedResultIndex);

			if (closestResult !== undefined && !_.isNil(closestResult?.pageIndex)) {
				const newViewState = pspdfkitInstance.viewState.set(
					'currentPageIndex',
					closestResult.pageIndex
				);
				pspdfkitInstance.setViewState(newViewState);
				debouncedTrackSearchedInsideEvent(query, mindItem);
			}
			pspdfkitInstance.setSearchState(newSearchState);
		});
	};

	const calculateActualIndex = (index: number) => {
		if (numberOfResults === undefined) {
			return -1;
		}
		if (index < 0) {
			return numberOfResults - 1;
		}
		if (index >= numberOfResults) {
			return 0;
		}
		return index;
	};

	const focusOnNextSearchResult =
		searchResultIndex !== undefined ? () => focusOnSearchResult(searchResultIndex + 1) : undefined;

	const focusOnPreviousSearchResult =
		searchResultIndex !== undefined ? () => focusOnSearchResult(searchResultIndex - 1) : undefined;

	const focusOnSearchResult = (index: number) => {
		if (!pspdfkitInstance || !numberOfResults) {
			return;
		}
		const actualIndex = calculateActualIndex(index);
		const searchState = pspdfkitInstance.searchState;
		const newSearchState = searchState.set('focusedResultIndex', actualIndex);
		pspdfkitInstance.setSearchState(newSearchState);

		const location = searchState.results.get(actualIndex)?.rectsOnPage?.get(0);
		const pageIndex = searchState.results.get(actualIndex)?.pageIndex;
		if (!_.isNil(pageIndex) && !_.isNil(location)) {
			pspdfkitInstance.jumpToRect(pageIndex, location);
		}
		setSearchResultIndex(actualIndex);
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	const searchInputProps = useMemo(
		() => ({
			onSearchInputChange: (event: React.ChangeEvent<HTMLInputElement>) => {
				handleChangeEvent(event);
				handleSearch(pspdfkitInstance, event.target.value);
			},
			onSearchNext: focusOnNextSearchResult,
			onSearchPrevious: focusOnPreviousSearchResult,
			endAdornment: getAdornment(),
			...(!isTextBased && {
				isDisabled: true,
				placeholder: t('Search is not possible'),
			}),
			value: searchTerm,
		}),
		[
			// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
			handleChangeEvent,
			focusOnNextSearchResult,
			focusOnPreviousSearchResult,
			// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
			getAdornment,
			isTextBased,
			t,
			searchTerm,
		]
	);

	const getFullScreenSx: () => {
		justifyContent: string;
		marginRight?: string;
	} = useCallback(() => {
		let sx = {
			justifyContent: 'right',
		};

		if (isIntercomVisible) {
			sx = Object.assign(sx, {
				marginRight: `${Number.parseInt(customSizes.intercomOffset.x) + Number.parseInt(spaces.md)}px`,
			});
		} else {
			sx = Object.assign(sx, { marginRight: customSizes.readerPage.paddingX });
		}

		return sx;
	}, [isIntercomVisible]);

	return (
		<Box
			component="div"
			sx={{
				position: 'absolute',
				bottom: spaces.md,
				width: '100%',
				display: 'flex',
				justifyContent: 'center',
				...(fullScreenMode && getFullScreenSx()),
			}}
		>
			<Box
				role="toolbar"
				sx={{
					padding: customSizes.floatingToolbar.padding,
					gap: spaces.xs,
					background: palette.background.paperElevation2,
					boxShadow: boxShadows.elevation8,
					borderRadius: borderRadius.sm,
					display: 'flex',
					alignItems: 'center',
					width: 'fit-content',
					justifyContent: 'center',
					flexDirection: 'row',
					height: customSizes.floatingToolbar.height,
					...(!isDesktopScreen && { zIndex: 1301 }),
				}}
			>
				{isDesktopScreen && <FloatingBarLargeContent searchInputProps={searchInputProps} />}
				{!isDesktopScreen && <FloatingBarSmallContent searchInputProps={searchInputProps} />}
			</Box>
		</Box>
	);
};

export interface FloatingToolbarCommonProps {
	searchInputProps?: {
		onSearchInputChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
		onSearchNext?: () => void;
		onSearchPrevious?: () => void;
		endAdornment?: string;
		placeholder?: string;
		isDisabled?: boolean;
		value?: string;
	};
}

export default FloatingToolbarBase;
